import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { alert } from 'actions/flash'
import {
  isOverrideLocked,
  OrderHandoff,
  ServiceCharge,
  ServiceChargeHealth,
  UpdateServiceChargePayload,
  useGetServiceChargesQuery,
  useRefreshServiceChargesMutation,
  useUpdateServiceChargeMutation,
} from 'api/serviceCharges'
import palette from 'constants/palette'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import startCase from 'lodash/startCase'
import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatServiceChargeAmount } from '../../serviceChargeFormat'
import { toIssues } from '../helpers'
import IssuePanel from '../IssuePanel'
import ServiceChargeEditModal from './ServiceChargeEditModal'
import ServiceChargeTable from './ServiceChargeTable'
import SyncFooter from './SyncFooter'
import { useServiceChargePermissions } from './useServiceChargePermissions'

const t = buildTranslate('locations.location_list.location_detail')

const SYNC_POLL_INTERVAL_MS = 3000
export const MAX_SYNC_POLL_ATTEMPTS = 10

type PendingSync = { baseline: string | null; attempts: number }

// Placeholder validator string (no real backend validator emits it): mirrors
// locationErrorCopy's MATCHERS shape so the delivery-fee guardrail routes
// through the existing IssuePanel pipeline instead of a new banner.
const SERVICE_CHARGE_HEALTH_VALIDATOR =
  'Service charge configuration has an unresolved error'

// Defaults an absent/never-computed health to 'unknown', never 'healthy' —
// avoids a false-positive from simply-missing data.
export const resolveServiceChargeHealth = (
  health: ServiceChargeHealth | undefined
): ServiceChargeHealth => health ?? 'unknown'

const describeSilencedCriteria = (charge: ServiceCharge): string => {
  const toast = charge.provider_criteria.toast
  if (!toast) return ''
  return (['delivery', 'takeout', 'dine_in'] as const)
    .filter(key => toast[key])
    .join(', ')
}

const buildSilencedChargeMessage = (charge: ServiceCharge): string =>
  t('service_charges.health_issue.silenced_charge', {
    amount: formatServiceChargeAmount(charge),
    name: charge.name,
    criteria: describeSilencedCriteria(charge),
  }) as string

type Props = { locationId: number }

const ServiceCharges = ({ locationId }: Props) => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const merchant = useCurrentMerchant()
  const flag = useFlag('manage-service-charges-mx', false)
  // Independent of `manage-service-charges-mx` so the bulk catalogue can
  // roll out on its own schedule.
  const showBulkManageLink = useFlag('service-charges-bulk-manage-mx', false)
  const canAccess = flag && Boolean(merchant?.supports_service_charges)
  const { sectionHidden, editDisabled, editDisabledReason } =
    useServiceChargePermissions()
  const skip = !canAccess || sectionHidden

  const [pendingSync, setPendingSync] = useState<PendingSync | null>(null)
  const { data, isLoading, isError, isFetching } = useGetServiceChargesQuery(
    { locationId },
    { skip, pollingInterval: pendingSync ? SYNC_POLL_INTERVAL_MS : 0 }
  )
  const [refresh, { isLoading: isSyncing }] = useRefreshServiceChargesMutation()
  const [updateCharge] = useUpdateServiceChargeMutation()

  const [editingId, setEditingId] = useState<number | null>(null)
  const [draftHandoffs, setDraftHandoffs] = useState<OrderHandoff[]>([])
  const [editingOriginalHandoffs, setEditingOriginalHandoffs] = useState<
    OrderHandoff[] | null
  >(null)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set())

  const lastSyncedAt = data?.synced_at ?? null
  const providerName = startCase(merchant?.ordering_provider ?? '')

  const wasFetchingRef = useRef(isFetching)
  useEffect(() => {
    const pollCycleSettled = wasFetchingRef.current && !isFetching
    wasFetchingRef.current = isFetching
    if (!pendingSync || !pollCycleSettled) return
    if (lastSyncedAt !== pendingSync.baseline) {
      setPendingSync(null)
    } else if (pendingSync.attempts + 1 >= MAX_SYNC_POLL_ATTEMPTS) {
      setPendingSync(null)
    } else {
      setPendingSync({ ...pendingSync, attempts: pendingSync.attempts + 1 })
    }
  }, [isFetching, lastSyncedAt, pendingSync])

  if (!canAccess || sectionHidden) return null

  const charges: ServiceCharge[] = data?.data ?? []
  const idsLockedThroughRefetch = isFetching
    ? new Set(charges.map(charge => charge.id))
    : pendingIds
  const health = resolveServiceChargeHealth(data?.location_health?.[0]?.health)
  const silencedCharges = charges.filter(
    charge =>
      isOverrideLocked(charge) && charge.criteria_override.handoffs.length > 0
  )
  const healthIssues =
    health === 'errored'
      ? toIssues(
          [
            SERVICE_CHARGE_HEALTH_VALIDATOR,
            ...silencedCharges.map(buildSilencedChargeMessage),
          ],
          merchant?.ordering_provider
        )
      : []

  const startEditing = (charge: ServiceCharge) => {
    setEditingId(charge.id)
    setDraftHandoffs([...charge.criteria_override.handoffs])
    setEditingOriginalHandoffs([...charge.criteria_override.handoffs])
  }

  const cancelEditing = () => {
    setEditingId(null)
    setDraftHandoffs([])
    setEditingOriginalHandoffs(null)
  }

  const sameHandoffs = (a: OrderHandoff[], b: OrderHandoff[]) =>
    a.length === b.length && a.every(handoff => b.includes(handoff))

  const onSaveOverrides = async () => {
    if (editingId === null || !editingOriginalHandoffs) return
    const charge = charges.find(c => c.id === editingId)
    if (!charge) return
    if (sameHandoffs(editingOriginalHandoffs, draftHandoffs)) {
      cancelEditing()
      return
    }
    setSavingId(charge.id)
    try {
      await updateCharge({
        id: charge.id,
        location_id: locationId,
        criteria_override: { handoffs: draftHandoffs },
      }).unwrap()
      dispatch(
        alert({ key: 'success', message: t('service_charges.save_success') })
      )
      cancelEditing()
    } catch {
      dispatch(
        alert({ key: 'danger', message: t('service_charges.save_error') })
      )
    } finally {
      setSavingId(null)
    }
  }

  const saveImmediately = async (
    patch: Omit<UpdateServiceChargePayload, 'location_id'>
  ) => {
    setPendingIds(prev => new Set(prev).add(patch.id))
    try {
      await updateCharge({ location_id: locationId, ...patch }).unwrap()
      dispatch(
        alert({ key: 'success', message: t('service_charges.save_success') })
      )
    } catch {
      dispatch(
        alert({ key: 'danger', message: t('service_charges.save_error') })
      )
    } finally {
      setPendingIds(prev => {
        const next = new Set(prev)
        next.delete(patch.id)
        return next
      })
    }
  }

  const onToggleThanxEnabled = (charge: ServiceCharge, next: boolean) => {
    void saveImmediately({ id: charge.id, thanx_enabled: next })
  }

  const onToggleEligibleForRedemption = (
    charge: ServiceCharge,
    next: boolean
  ) => {
    void saveImmediately({ id: charge.id, eligible_for_redemption: next })
  }

  const onToggleHandoff = (
    charge: ServiceCharge,
    handoff: OrderHandoff,
    next: boolean
  ) => {
    if (editingId !== charge.id) return
    setDraftHandoffs(prev =>
      next ? [...prev, handoff] : prev.filter(h => h !== handoff)
    )
  }

  const onSyncNow = async () => {
    try {
      await refresh({ locationId }).unwrap()
      setPendingSync({ baseline: lastSyncedAt, attempts: 0 })
      dispatch(
        alert({
          key: 'success',
          message: t('service_charges.sync.started', {
            provider: providerName,
          }),
        })
      )
    } catch {
      dispatch(
        alert({ key: 'danger', message: t('service_charges.sync.error') })
      )
    }
  }

  const editingCharge = charges.find(c => c.id === editingId) ?? null

  const renderBody = (): React.ReactNode => {
    if (isLoading) {
      return (
        <p
          className={`body-text-4 mb-0 ${css({ color: palette.grey70 })}`}
          role="status"
        >
          {t('service_charges.loading')}
        </p>
      )
    }
    if (isError) {
      return (
        <div data-testid="service-charges-fetch-error" role="alert">
          <Notification kind="negative" size="full">
            <Text.BodyText4 color="grey70" className="p-xs" tag="div">
              {t('service_charges.fetch_error')}
            </Text.BodyText4>
          </Notification>
        </div>
      )
    }
    return (
      <>
        {healthIssues.length > 0 && (
          <div role="alert">
            {healthIssues.map(issue => (
              <IssuePanel key={issue.id} issue={issue} />
            ))}
          </div>
        )}
        {charges.length === 0 ? (
          <p className={`body-text-4 mb-0 ${css({ color: palette.grey70 })}`}>
            {t('service_charges.empty')}
          </p>
        ) : (
          <ServiceChargeTable
            charges={charges}
            editDisabled={editDisabled}
            editDisabledReason={editDisabledReason}
            onToggleThanxEnabled={onToggleThanxEnabled}
            onToggleEligibleForRedemption={onToggleEligibleForRedemption}
            editingId={editingId}
            onStartEditing={startEditing}
            lockedIds={idsLockedThroughRefetch}
          />
        )}
        <ServiceChargeEditModal
          charge={editingCharge}
          draftHandoffs={draftHandoffs}
          onToggleHandoff={(handoff, next) =>
            editingCharge && onToggleHandoff(editingCharge, handoff, next)
          }
          onCancel={cancelEditing}
          onSave={onSaveOverrides}
          isSaving={savingId !== null}
        />
        <SyncFooter
          syncedAt={lastSyncedAt}
          isSyncing={isSyncing}
          onSyncNow={onSyncNow}
          providerName={providerName}
        />
      </>
    )
  }

  return (
    <section className="mb-xl" data-testid="service-charges-section">
      <div className="d-flex justify-content-between align-items-center mb-xs">
        <Text.Header4 className={css({ color: palette.grey90 })}>
          {t('service_charges.title')}
        </Text.Header4>
        {showBulkManageLink && (
          <Link
            to={{
              pathname: '/service_charges',
              state: { fromLocationId: locationId },
            }}
            className={css({
              color: palette.belizeHole,
              fontWeight: 700,
              fontSize: '13px',
              whiteSpace: 'nowrap',
            })}
          >
            {t('service_charges.manage_link')}
          </Link>
        )}
      </div>

      {renderBody()}
    </section>
  )
}

export default ServiceCharges
